import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/AuthContext";
import LoginPage from "@/LoginPage";
import HelpPage from "@/HelpPage";
import { 
  Mail, 
  Download, 
  RefreshCw, 
  Trash2, 
  Tag, 
  Building2, 
  User, 
  Globe,
  Check,
  X,
  ChevronDown,
  Search,
  Filter,
  FileSpreadsheet,
  Inbox,
  Users,
  Loader2,
  Plus,
  ExternalLink,
  Settings,
  GripVertical,
  ArrowUp,
  ArrowDown,
  Bell,
  Clock,
  Calendar,
  Zap,
  Play,
  History,
  Eye,
  EyeOff,
  Lock,
  KeyRound,
  ShieldCheck,
  HelpCircle,
  FileText,
  Receipt,
  ShoppingBag,
  Plane,
  CreditCard,
  Package,
  FileCheck,
  Ticket,
  Sparkles,
  DollarSign,
  LogOut
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Toaster, toast } from "sonner";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const BACKEND_URL = process.env.REACT_APP_API_BASE || process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL ? `${BACKEND_URL.replace(/\/$/, '')}/api` : '';

const OAUTH_ERROR_MESSAGES = {
  access_denied: "Connection was cancelled. No email access was granted.",
  invalid_state: "The connection request expired. Please try again.",
  missing_code: "The email provider did not complete the connection. Please try again.",
  no_user_session: "Your Email Extractor session expired. Please sign in and try again.",
  token_exchange_failed: "The provider could not finish connecting your account. Please try again.",
  user_info_failed: "The provider connected, but your account details could not be read. Please try again."
};

const APP_PASSWORD_GUIDES = {
  gmail: {
    label: "Google / Gmail",
    url: "https://myaccount.google.com/apppasswords",
    note: "Turn on 2-Step Verification, then create an app password for Mail."
  },
  outlook: {
    label: "Microsoft / Outlook",
    url: "https://account.microsoft.com/security",
    note: "Your organization may need to allow app passwords or IMAP access."
  },
  yahoo: {
    label: "Yahoo Mail",
    url: "https://login.yahoo.com/account/security",
    note: "Generate an app password in Account Security."
  },
  icloud: {
    label: "Apple iCloud Mail",
    url: "https://account.apple.com/sign-in",
    note: "Create an app-specific password in Sign-In and Security."
  },
  godaddy: {
    label: "GoDaddy Workspace",
    url: "https://sso.godaddy.com/",
    note: "Use the credentials and IMAP access provided by your GoDaddy account."
  },
  auto: {
    label: "Other business email",
    url: null,
    note: "Ask your email administrator for an app password and confirm that IMAP is enabled."
  }
};

const inferProviderFromEmail = (emailAddress) => {
  const domain = emailAddress.trim().toLowerCase().split('@')[1] || '';
  if (domain.includes('gmail')) return 'gmail';
  if (['outlook.com', 'hotmail.com', 'live.com', 'msn.com'].includes(domain)) return 'outlook';
  if (domain.includes('yahoo')) return 'yahoo';
  if (['icloud.com', 'me.com', 'mac.com'].includes(domain)) return 'icloud';
  if (domain.includes('secureserver') || domain.includes('godaddy')) return 'godaddy';
  return 'auto';
};

// Google Icon Component
const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

// Microsoft Icon Component
const MicrosoftIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5">
    <path fill="#F25022" d="M1 1h10v10H1z"/>
    <path fill="#00A4EF" d="M1 13h10v10H1z"/>
    <path fill="#7FBA00" d="M13 1h10v10H13z"/>
    <path fill="#FFB900" d="M13 13h10v10H13z"/>
  </svg>
);

function App() {
  const {
    user,
    loading: authLoading,
    logout,
    targetGroupId,
    importToProEmail
  } = useAuth();
  
  // Show login page if not authenticated
  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }
  
  if (!user) {
    return <LoginPage />;
  }
  
  return (
    <MainApp
      user={user}
      logout={logout}
      targetGroupId={targetGroupId}
      importToProEmail={importToProEmail}
    />
  );
}

function MainApp({ user, logout, targetGroupId, importToProEmail }) {
  const [accounts, setAccounts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState(null);
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterDomain, setFilterDomain] = useState("");
  const [selectedContacts, setSelectedContacts] = useState(new Set());
  const [editingAccount, setEditingAccount] = useState(null);
  const [showExportSettings, setShowExportSettings] = useState(false);
  const [pushingToProEmail, setPushingToProEmail] = useState(false);
  const [availableFields, setAvailableFields] = useState([]);
  const [exportColumns, setExportColumns] = useState([
    { field: "name", header: "Full Name", enabled: true },
    { field: "email", header: "email", enabled: true }
  ]);
  const [showSyncSettings, setShowSyncSettings] = useState(false);
  const [syncSettings, setSyncSettings] = useState({
    enabled: false,
    frequency: 'daily',
    sync_time: '06:00',
    sync_day: 0,
    notify_new_business: true,
    auto_extract_source: 'all'
  });
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [syncHistory, setSyncHistory] = useState([]);
  const [showHelpPage, setShowHelpPage] = useState(false);
  
  // Login form state
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginTag, setLoginTag] = useState("both");
  const [loginProvider, setLoginProvider] = useState("auto");
  const [detectedManualProvider, setDetectedManualProvider] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [oauthConnecting, setOauthConnecting] = useState(null);
  const [connectionMethods, setConnectionMethods] = useState({
    google: false,
    microsoft: false,
    manual: true,
    loaded: false
  });
  const [showManualConnection, setShowManualConnection] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  
  // Documents state
  const [activeTab, setActiveTab] = useState("contacts");
  const [documents, setDocuments] = useState([]);
  const [documentCategories, setDocumentCategories] = useState({});
  const [documentCategory, setDocumentCategory] = useState("all");
  const [documentSearch, setDocumentSearch] = useState("");
  const [scanningDocs, setScanningDocs] = useState(false);
  const [documentStats, setDocumentStats] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);

  // Fetch accounts and contacts on load
  useEffect(() => {
    fetchAccounts();
    fetchContacts();
    fetchStats();
    fetchExportFields();
    fetchSyncSettings();
    fetchNotifications();
    fetchConnectionMethods();
    
    // Poll for notifications every 30 seconds
    const notifInterval = setInterval(fetchNotifications, 30000);
    
    // Check for OAuth callback
    const params = new URLSearchParams(window.location.search);
    if (params.get('connected')) {
      toast.success(`Successfully connected ${params.get('connected')} account!`);
      window.history.replaceState({}, '', window.location.pathname);
      fetchAccounts();
    }
    if (params.get('error')) {
      const errorCode = params.get('error');
      toast.error(OAUTH_ERROR_MESSAGES[errorCode] || params.get('message') || "The email account could not be connected.");
      window.history.replaceState({}, '', window.location.pathname);
    }
    
    return () => clearInterval(notifInterval);
  }, []);

  const fetchExportFields = async () => {
    try {
      const response = await axios.get(`${API}/export/fields`);
      setAvailableFields(response.data.available_fields || []);
    } catch (error) {
      console.error("Failed to fetch export fields", error);
    }
  };

  const fetchSyncSettings = async () => {
    try {
      const response = await axios.get(`${API}/sync/settings`);
      setSyncSettings(response.data);
    } catch (error) {
      console.error("Failed to fetch sync settings", error);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`);
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error("Failed to fetch notifications", error);
    }
  };

  const fetchSyncHistory = async () => {
    try {
      const response = await axios.get(`${API}/sync/history?limit=5`);
      setSyncHistory(response.data.history || []);
    } catch (error) {
      console.error("Failed to fetch sync history", error);
    }
  };

  const fetchAccounts = async () => {
    try {
      const response = await axios.get(`${API}/accounts`);
      setAccounts(response.data.accounts || []);
    } catch (error) {
      console.error("Failed to fetch accounts", error);
    }
  };

  const fetchConnectionMethods = async () => {
    try {
      const response = await axios.get(`${API}/auth/providers`);
      const methods = {
        google: Boolean(response.data?.google?.available),
        microsoft: Boolean(response.data?.microsoft?.available),
        manual: response.data?.manual?.available !== false,
        loaded: true
      };
      setConnectionMethods(methods);
      if (!methods.google && !methods.microsoft) {
        setShowManualConnection(true);
      }
    } catch (error) {
      console.error("Failed to fetch connection methods", error);
      setConnectionMethods(current => ({ ...current, loaded: true }));
      setShowManualConnection(true);
    }
  };

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterType !== 'all') params.append('filter_type', filterType);
      if (filterDomain) params.append('domain', filterDomain);
      
      const response = await axios.get(`${API}/contacts?${params.toString()}`);
      setContacts(response.data.contacts || []);
      setDomains(response.data.domains || []);
    } catch (error) {
      console.error("Failed to fetch contacts", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats", error);
    }
  };

  const fetchDocuments = async () => {
    try {
      const params = new URLSearchParams();
      if (documentCategory && documentCategory !== 'all') params.append('category', documentCategory);
      if (documentSearch) params.append('search', documentSearch);
      
      const response = await axios.get(`${API}/documents?${params.toString()}`);
      setDocuments(response.data.documents || []);
      setDocumentCategories(response.data.categories || {});
    } catch (error) {
      console.error("Failed to fetch documents", error);
    }
  };

  const fetchDocumentStats = async () => {
    try {
      const response = await axios.get(`${API}/documents/stats/summary`);
      setDocumentStats(response.data);
    } catch (error) {
      console.error("Failed to fetch document stats", error);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, [filterType, filterDomain]);

  useEffect(() => {
    if (activeTab === 'documents') {
      fetchDocuments();
      fetchDocumentStats();
    }
  }, [activeTab, documentCategory, documentSearch]);

  const detectManualProvider = async () => {
    if (loginProvider !== 'auto' || !loginEmail.includes('@')) return;
    try {
      const response = await axios.get(`${API}/auth/detect-provider`, {
        params: { email: loginEmail.trim() }
      });
      setDetectedManualProvider(response.data?.provider || null);
    } catch (error) {
      setDetectedManualProvider(null);
    }
  };

  const connectAccount = async (e) => {
    e.preventDefault();
    
    if (!loginEmail || !loginPassword) {
      toast.error("Please enter your email address and app password.");
      return;
    }
    
    setConnecting(true);
    try {
      const response = await axios.post(`${API}/auth/simple-login`, {
        email: loginEmail,
        password: loginPassword,
        tag: loginTag,
        provider: loginProvider
      });
      
      toast.success(response.data.message);
      setLoginEmail("");
      setLoginPassword("");
      setLoginTag("both");
      setLoginProvider("auto");
      setDetectedManualProvider(null);
      fetchAccounts();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to connect account");
    } finally {
      setConnecting(false);
    }
  };

  const connectGoogle = async () => {
    setOauthConnecting('google');
    try {
      const response = await axios.get(`${API}/auth/google/login`);
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Google connection is unavailable. Please use secure app-password setup.");
      setOauthConnecting(null);
    }
  };

  const connectMicrosoft = async () => {
    setOauthConnecting('microsoft');
    try {
      const response = await axios.get(`${API}/auth/microsoft/login`);
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Microsoft connection is unavailable. Please use secure app-password setup.");
      setOauthConnecting(null);
    }
  };

  const disconnectAccount = async (accountId) => {
    try {
      await axios.delete(`${API}/accounts/${accountId}`);
      toast.success("Account disconnected");
      fetchAccounts();
      fetchContacts();
      fetchStats();
    } catch (error) {
      toast.error("Failed to disconnect account");
    }
  };

  const updateAccountTag = async (accountId, tag) => {
    try {
      await axios.put(`${API}/accounts/${accountId}/tag`, { tag });
      toast.success("Account tag updated");
      fetchAccounts();
      setEditingAccount(null);
    } catch (error) {
      toast.error("Failed to update tag");
    }
  };

  const extractContacts = async (accountId, source = 'all') => {
    setExtracting(true);
    try {
      const response = await axios.post(`${API}/extract/${accountId}?source=${source}`);
      toast.success(`Extracted ${response.data.extracted} contacts (${response.data.new} new)!`);
      fetchContacts();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to extract contacts");
    } finally {
      setExtracting(false);
    }
  };

  // Document scan settings
  const [scanTimeRange, setScanTimeRange] = useState('180'); // days
  const [showScanOptions, setShowScanOptions] = useState(false);
  const [scanLimit, setScanLimit] = useState(100);

  const scanDocuments = async (accountId) => {
    setScanningDocs(true);
    try {
      const response = await axios.post(
        `${API}/documents/scan/${accountId}?limit=${scanLimit}&days_back=${scanTimeRange}`
      );
      const { scanned, extracted, skipped_existing, date_range } = response.data;
      toast.success(
        `Scanned ${scanned} emails from ${date_range?.from?.split('T')[0] || 'N/A'} to ${date_range?.to?.split('T')[0] || 'today'}. ` +
        `Found ${extracted} new documents${skipped_existing ? `, ${skipped_existing} already processed` : ''}.`
      );
      fetchDocuments();
      fetchDocumentStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to scan documents");
    } finally {
      setScanningDocs(false);
    }
  };

  const reconnectForDocumentScan = (account) => {
    setLoginEmail(account.email || "");
    setLoginProvider(account.provider || "auto");
    setShowManualConnection(true);
    setActiveTab("contacts");
    window.scrollTo({ top: 0, behavior: "smooth" });
    toast.info("Enter a new app password once to reconnect this email account.");
  };

  const exportDocuments = async () => {
    try {
      const response = await axios.post(
        `${API}/documents/export?category=${documentCategory}&format=csv`,
        {},
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `documents_export_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Documents exported successfully!");
    } catch (error) {
      toast.error("Failed to export documents");
    }
  };

  const deleteDocument = async (docId) => {
    try {
      await axios.delete(`${API}/documents/${docId}`);
      toast.success("Document deleted");
      fetchDocuments();
      fetchDocumentStats();
    } catch (error) {
      toast.error("Failed to delete document");
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      invoice: <FileText className="w-4 h-4" />,
      receipt: <Receipt className="w-4 h-4" />,
      order: <ShoppingBag className="w-4 h-4" />,
      booking: <Plane className="w-4 h-4" />,
      payment: <CreditCard className="w-4 h-4" />,
      shipping: <Package className="w-4 h-4" />,
      subscription: <RefreshCw className="w-4 h-4" />,
      ticket: <Ticket className="w-4 h-4" />,
      contract: <FileCheck className="w-4 h-4" />,
      other: <FileSpreadsheet className="w-4 h-4" />
    };
    return icons[category] || icons.other;
  };

  const getCategoryColor = (category) => {
    const colors = {
      invoice: 'text-blue-400 bg-blue-500/10 border-blue-500/20',
      receipt: 'text-green-400 bg-green-500/10 border-green-500/20',
      order: 'text-purple-400 bg-purple-500/10 border-purple-500/20',
      booking: 'text-cyan-400 bg-cyan-500/10 border-cyan-500/20',
      payment: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20',
      shipping: 'text-orange-400 bg-orange-500/10 border-orange-500/20',
      subscription: 'text-pink-400 bg-pink-500/10 border-pink-500/20',
      ticket: 'text-indigo-400 bg-indigo-500/10 border-indigo-500/20',
      other: 'text-zinc-400 bg-zinc-500/10 border-zinc-500/20'
    };
    return colors[category] || colors.other;
  };

  const exportCSV = async () => {
    try {
      // Build columns array from enabled export columns
      const columns = exportColumns
        .filter(col => col.enabled)
        .map(col => ({ field: col.field, header: col.header }));
      
      const response = await axios.post(`${API}/export/csv`, {
        filter_type: filterType !== 'all' ? filterType : null,
        contact_ids: selectedContacts.size > 0 ? Array.from(selectedContacts) : null,
        columns: columns.length > 0 ? columns : null
      }, { responseType: 'blob' });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contacts_export_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("CSV exported successfully!");
      setShowExportSettings(false);
    } catch (error) {
      toast.error("Failed to export CSV");
    }
  };

  const pushSelectedToProEmail = async () => {
    if (!targetGroupId) {
      toast.error("Open Email Extractor from a Pro-Email group before pushing contacts.");
      return;
    }

    try {
      setPushingToProEmail(true);
      const contactIds = selectedContacts.size > 0
        ? Array.from(selectedContacts)
        : null;
      const result = await importToProEmail(contactIds);
      const imported = result?.imported || 0;
      const skipped = result?.skipped_duplicates || 0;

      toast.success(
        `${imported} contact${imported === 1 ? "" : "s"} pushed to Pro-Email${skipped ? ` (${skipped} duplicate${skipped === 1 ? "" : "s"} skipped)` : ""}.`
      );

      if (result?.redirect_url) {
        window.setTimeout(() => window.location.assign(result.redirect_url), 700);
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to push contacts to Pro-Email.");
    } finally {
      setPushingToProEmail(false);
    }
  };

  const toggleExportColumn = (field) => {
    setExportColumns(prev => 
      prev.map(col => 
        col.field === field ? { ...col, enabled: !col.enabled } : col
      )
    );
  };

  const updateColumnHeader = (field, newHeader) => {
    setExportColumns(prev =>
      prev.map(col =>
        col.field === field ? { ...col, header: newHeader } : col
      )
    );
  };

  const addExportColumn = (field) => {
    const fieldInfo = availableFields.find(f => f.field === field);
    if (fieldInfo && !exportColumns.find(c => c.field === field)) {
      setExportColumns(prev => [...prev, {
        field: field,
        header: fieldInfo.default_header,
        enabled: true
      }]);
    }
  };

  const removeExportColumn = (field) => {
    setExportColumns(prev => prev.filter(col => col.field !== field));
  };

  const moveColumn = (index, direction) => {
    const newColumns = [...exportColumns];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex >= 0 && newIndex < newColumns.length) {
      [newColumns[index], newColumns[newIndex]] = [newColumns[newIndex], newColumns[index]];
      setExportColumns(newColumns);
    }
  };

  const resetToDefault = () => {
    setExportColumns([
      { field: "name", header: "Full Name", enabled: true },
      { field: "email", header: "email", enabled: true }
    ]);
  };

  const updateSyncSettings = async (updates) => {
    try {
      const response = await axios.put(`${API}/sync/settings`, updates);
      setSyncSettings(response.data);
      toast.success("Sync settings updated");
    } catch (error) {
      toast.error("Failed to update sync settings");
    }
  };

  const triggerSyncNow = async () => {
    setSyncing(true);
    try {
      await axios.post(`${API}/sync/now`);
      toast.success("Sync started! You'll be notified when complete.");
      // Poll for completion
      setTimeout(() => {
        fetchStats();
        fetchContacts();
        fetchNotifications();
        setSyncing(false);
      }, 5000);
    } catch (error) {
      toast.error("Failed to start sync");
      setSyncing(false);
    }
  };

  const markNotificationRead = async (id) => {
    try {
      await axios.put(`${API}/notifications/${id}/read`);
      fetchNotifications();
    } catch (error) {
      console.error("Failed to mark notification read", error);
    }
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`);
      fetchNotifications();
    } catch (error) {
      console.error("Failed to mark all read", error);
    }
  };

  const filteredContacts = contacts.filter(contact => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return contact.name?.toLowerCase().includes(search) || 
           contact.email?.toLowerCase().includes(search) ||
           contact.domain?.toLowerCase().includes(search);
  });

  const toggleSelectAll = () => {
    if (selectedContacts.size === filteredContacts.length) {
      setSelectedContacts(new Set());
    } else {
      setSelectedContacts(new Set(filteredContacts.map(c => c.id)));
    }
  };

  const toggleContact = (id) => {
    const newSelected = new Set(selectedContacts);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedContacts(newSelected);
  };

  const inferredProvider = inferProviderFromEmail(loginEmail);
  const effectiveManualProvider = loginProvider === 'auto'
    ? (detectedManualProvider || inferredProvider)
    : loginProvider;
  const manualGuide = APP_PASSWORD_GUIDES[effectiveManualProvider] || APP_PASSWORD_GUIDES.auto;
  const hasOAuthConnection = connectionMethods.google || connectionMethods.microsoft;

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#09090b]">
      <Toaster theme="dark" position="top-right" />
      
      {/* Help Page (Full Screen) */}
      {showHelpPage && (
        <HelpPage onBack={() => setShowHelpPage(false)} />
      )}
      
      {/* Main App (Hidden when Help is shown) */}
      {!showHelpPage && (
        <>
      {/* Header */}
      <header className="border-b border-white/10 bg-zinc-950/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="mx-auto max-w-[1600px] px-4 py-3 sm:px-6">
          <div className="flex flex-col gap-3 2xl:flex-row 2xl:items-center 2xl:justify-between">
            <div className="flex shrink-0 items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center">
                <Mail className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-semibold font-['Outfit'] text-white">Email Extractor</h1>
                <p className="text-xs text-zinc-500">Extract contacts from your email accounts</p>
              </div>
            </div>
            
            <div className="flex w-full flex-wrap items-center gap-2 2xl:w-auto 2xl:justify-end">
              {/* Notifications */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="relative"
                    data-testid="notifications-btn"
                  >
                    <Bell className="w-5 h-5" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center">
                        {unreadCount}
                      </span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80 bg-zinc-900 border-white/10 p-0" align="end">
                  <div className="p-3 border-b border-white/10 flex items-center justify-between">
                    <h3 className="font-semibold text-white">Notifications</h3>
                    {unreadCount > 0 && (
                      <Button variant="ghost" size="sm" onClick={markAllRead} className="text-xs text-indigo-400">
                        Mark all read
                      </Button>
                    )}
                  </div>
                  <ScrollArea className="h-[300px]">
                    {notifications.length === 0 ? (
                      <div className="p-4 text-center text-zinc-500 text-sm">
                        No notifications yet
                      </div>
                    ) : (
                      <div className="divide-y divide-white/5">
                        {notifications.map((notif) => (
                          <div 
                            key={notif.id}
                            className={`p-3 hover:bg-white/5 cursor-pointer ${!notif.read ? 'bg-indigo-500/5' : ''}`}
                            onClick={() => markNotificationRead(notif.id)}
                          >
                            <div className="flex items-start gap-3">
                              <div className={`w-2 h-2 rounded-full mt-2 ${!notif.read ? 'bg-indigo-500' : 'bg-zinc-600'}`} />
                              <div className="flex-1">
                                <p className="font-medium text-sm text-white">{notif.title}</p>
                                <p className="text-xs text-zinc-400 mt-1">{notif.message}</p>
                                {notif.data?.contacts && (
                                  <div className="mt-2 space-y-1">
                                    {notif.data.contacts.slice(0, 3).map((c, i) => (
                                      <p key={i} className="text-xs text-zinc-500 font-mono">
                                        {c.name} ({c.domain})
                                      </p>
                                    ))}
                                    {notif.data.total > 3 && (
                                      <p className="text-xs text-indigo-400">+{notif.data.total - 3} more</p>
                                    )}
                                  </div>
                                )}
                                <p className="text-xs text-zinc-600 mt-2">
                                  {new Date(notif.created_at).toLocaleString()}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </PopoverContent>
              </Popover>

              {/* Sync Now Button */}
              <Button
                variant="outline"
                onClick={triggerSyncNow}
                disabled={syncing || accounts.length === 0}
                className="border-white/10 hover:bg-white/5"
                data-testid="sync-now-btn"
              >
                {syncing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Zap className="w-4 h-4 mr-2" />
                )}
                Sync Now
              </Button>

              {/* Sync Settings Button */}
              <Button
                variant="outline"
                onClick={() => { setShowSyncSettings(true); fetchSyncHistory(); }}
                className="border-white/10 hover:bg-white/5"
                data-testid="sync-settings-btn"
              >
                <Clock className="w-4 h-4 mr-2" />
                Auto Sync
                {syncSettings.enabled && (
                  <span className="ml-2 w-2 h-2 bg-emerald-500 rounded-full" />
                )}
              </Button>

              <Button 
                variant="outline"
                onClick={() => setShowExportSettings(true)}
                className="border-white/10 hover:bg-white/5"
                data-testid="export-settings-btn"
              >
                <Settings className="w-4 h-4 mr-2" />
                Export Settings
              </Button>
              
              {/* Help Button */}
              <Button 
                variant="outline"
                onClick={() => setShowHelpPage(true)}
                className="border-white/10 hover:bg-white/5"
                data-testid="help-btn"
              >
                <HelpCircle className="w-4 h-4 mr-2" />
                Help
              </Button>
              
              <Button 
                onClick={exportCSV} 
                disabled={contacts.length === 0}
                className="bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 glow-primary"
                data-testid="export-csv-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
                {selectedContacts.size > 0 && (
                  <Badge variant="secondary" className="ml-2 bg-white/20">
                    {selectedContacts.size}
                  </Badge>
                )}
              </Button>

              {targetGroupId && (
                <Button
                  onClick={pushSelectedToProEmail}
                  disabled={contacts.length === 0 || pushingToProEmail}
                  className="bg-emerald-600 hover:bg-emerald-500"
                  data-testid="push-to-pro-email-toolbar-btn"
                >
                  {pushingToProEmail ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <ExternalLink className="w-4 h-4 mr-2" />
                  )}
                  Push to Pro-Email
                  {selectedContacts.size > 0 && (
                    <Badge variant="secondary" className="ml-2 bg-white/20">
                      {selectedContacts.size}
                    </Badge>
                  )}
                </Button>
              )}
              
              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="ml-2">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center text-white text-sm font-medium">
                      {user?.name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase()}
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-zinc-900 border-white/10">
                  <div className="px-3 py-2 border-b border-white/10">
                    <p className="text-sm font-medium text-white">{user?.name}</p>
                    <p className="text-xs text-zinc-500">{user?.email}</p>
                  </div>
                  <DropdownMenuItem 
                    onClick={logout}
                    className="text-red-400 focus:text-red-300 focus:bg-red-500/10"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList className="bg-zinc-900/50 border border-white/10">
            <TabsTrigger value="contacts" className="data-[state=active]:bg-indigo-600" data-testid="tab-contacts">
              <Users className="w-4 h-4 mr-2" />
              Contacts
            </TabsTrigger>
            <TabsTrigger value="documents" className="data-[state=active]:bg-indigo-600" data-testid="tab-documents">
              <Sparkles className="w-4 h-4 mr-2" />
              Smart Documents
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {activeTab === 'contacts' && (
          <>
        {/* Stats Cards */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8"
        >
          <Card className="bg-zinc-900/50 border-white/10" data-testid="stat-accounts">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-zinc-500">Connected Accounts</p>
                  <p className="text-3xl font-bold font-['Outfit'] text-white">{stats?.connected_accounts || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-indigo-500/10 flex items-center justify-center">
                  <Globe className="w-6 h-6 text-indigo-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-zinc-900/50 border-white/10" data-testid="stat-total">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-zinc-500">Total Contacts</p>
                  <p className="text-3xl font-bold font-['Outfit'] text-white">{stats?.unique_emails || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-violet-500/10 flex items-center justify-center">
                  <Users className="w-6 h-6 text-violet-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-zinc-900/50 border-white/10" data-testid="stat-business">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-zinc-500">Business Emails</p>
                  <p className="text-3xl font-bold font-['Outfit'] text-white">{stats?.business_contacts || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-emerald-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-zinc-900/50 border-white/10" data-testid="stat-personal">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-zinc-500">Personal Emails</p>
                  <p className="text-3xl font-bold font-['Outfit'] text-white">{stats?.personal_contacts || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-orange-500/10 flex items-center justify-center">
                  <User className="w-6 h-6 text-orange-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Connected Accounts Sidebar */}
          <div className="min-w-0 lg:col-span-4">
            <Card className="bg-zinc-900/50 border-white/10" data-testid="accounts-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-['Outfit'] text-lg text-white">Connect an email account</CardTitle>
                    <CardDescription>Choose your provider to get started</CardDescription>
                  </div>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-8 w-8"
                          onClick={() => setShowHelp(true)}
                        >
                          <HelpCircle className="w-4 h-4 text-zinc-500" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent className="bg-zinc-800 border-white/10 max-w-xs">
                        <p className="text-xs">We request read-only email access to find contacts. You can disconnect at any time.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3 rounded-lg border border-emerald-500/20 bg-emerald-500/10 p-3">
                  <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-emerald-400" />
                  <div>
                    <p className="text-sm font-medium text-emerald-200">Secure, read-only connection</p>
                    <p className="mt-0.5 text-xs text-emerald-300/80">Email Extractor can read contacts and the email content you choose to scan. It cannot send or delete email.</p>
                  </div>
                </div>

                {!connectionMethods.loaded && (
                  <div className="flex items-center justify-center rounded-lg border border-white/10 p-6 text-sm text-zinc-400">
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Checking available providers…
                  </div>
                )}

                {connectionMethods.loaded && hasOAuthConnection && (
                  <div className="space-y-2" data-testid="oauth-connections">
                    <p className="text-xs font-medium uppercase tracking-wider text-zinc-500">Recommended — one-click connection</p>
                    {connectionMethods.google && (
                      <Button
                        variant="outline"
                        onClick={connectGoogle}
                        disabled={Boolean(oauthConnecting)}
                        className="h-12 w-full justify-start border-white/10 bg-zinc-950/40 hover:bg-white/5"
                        data-testid="connect-google-oauth"
                      >
                        {oauthConnecting === 'google' ? <Loader2 className="h-5 w-5 animate-spin" /> : <GoogleIcon />}
                        <span className="ml-3">Continue with Google</span>
                      </Button>
                    )}
                    {connectionMethods.microsoft && (
                      <Button
                        variant="outline"
                        onClick={connectMicrosoft}
                        disabled={Boolean(oauthConnecting)}
                        className="h-12 w-full justify-start border-white/10 bg-zinc-950/40 hover:bg-white/5"
                        data-testid="connect-microsoft-oauth"
                      >
                        {oauthConnecting === 'microsoft' ? <Loader2 className="h-5 w-5 animate-spin" /> : <MicrosoftIcon />}
                        <span className="ml-3">Continue with Microsoft</span>
                      </Button>
                    )}
                    <p className="px-1 text-xs text-zinc-500">Works with Gmail, Google Workspace, Outlook, Hotmail and Microsoft 365.</p>
                  </div>
                )}

                {connectionMethods.loaded && !hasOAuthConnection && (
                  <div className="rounded-lg border border-amber-500/20 bg-amber-500/10 p-3 text-sm text-amber-200" data-testid="oauth-unavailable">
                    One-click provider connection is being configured. Secure app-password setup is available below.
                  </div>
                )}

                {hasOAuthConnection && (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setShowManualConnection(value => !value)}
                    className="w-full text-zinc-400 hover:bg-white/5 hover:text-white"
                    data-testid="toggle-manual-connection"
                  >
                    <KeyRound className="mr-2 h-4 w-4" />
                    {showManualConnection ? 'Hide app-password setup' : 'Use another provider or app password'}
                  </Button>
                )}

                {showManualConnection && (
                <form onSubmit={connectAccount} className="space-y-3 rounded-lg border border-white/10 bg-zinc-950/30 p-4" data-testid="manual-connection-form">
                  <div>
                    <p className="text-sm font-medium text-white">Secure app-password setup</p>
                    <p className="mt-1 text-xs text-zinc-500">Never enter your normal email password. Create a separate app password with your provider.</p>
                  </div>

                  <div>
                    <Label className="text-zinc-400 text-xs mb-1.5 block">Email Provider</Label>
                    <Select value={loginProvider} onValueChange={(value) => {
                      setLoginProvider(value);
                      setDetectedManualProvider(null);
                    }}>
                      <SelectTrigger className="bg-zinc-950/50 border-white/10" data-testid="login-provider">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-white/10">
                        <SelectItem value="auto">Auto-detect / Business email</SelectItem>
                        <SelectItem value="gmail">Google / Gmail</SelectItem>
                        <SelectItem value="outlook">Microsoft 365 / Outlook</SelectItem>
                        <SelectItem value="yahoo">Yahoo Mail</SelectItem>
                        <SelectItem value="icloud">Apple iCloud Mail</SelectItem>
                        <SelectItem value="godaddy">GoDaddy Workspace</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-zinc-400 text-xs mb-1.5 block">Email Address</Label>
                    <Input
                      type="email"
                      placeholder="erwin@simple-avs.com"
                      value={loginEmail}
                      onChange={(e) => {
                        setLoginEmail(e.target.value);
                        setDetectedManualProvider(null);
                      }}
                      onBlur={detectManualProvider}
                      className="bg-zinc-950/50 border-white/10"
                      data-testid="login-email"
                    />
                  </div>

                  <div className="rounded-md border border-indigo-500/20 bg-indigo-500/10 p-3">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-medium text-indigo-200">{manualGuide.label}</p>
                      {loginProvider === 'auto' && detectedManualProvider && (
                        <span className="text-[10px] font-medium uppercase tracking-wide text-emerald-300" data-testid="detected-provider">Detected automatically</span>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-indigo-300/80">{manualGuide.note}</p>
                    {manualGuide.url && (
                      <a href={manualGuide.url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center text-xs font-medium text-indigo-300 hover:text-indigo-200 hover:underline">
                        Open app-password settings <ExternalLink className="ml-1 h-3 w-3" />
                      </a>
                    )}
                  </div>
                  
                  <div>
                    <Label className="text-zinc-400 text-xs mb-1.5 block">App Password</Label>
                    <div className="relative">
                      <Input
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••••••••••"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        className="bg-zinc-950/50 border-white/10 pr-10"
                        data-testid="login-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={showPassword ? "Hide app password" : "Show app password"}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-zinc-400 text-xs mb-1.5 block">Contacts to Extract</Label>
                    <Select value={loginTag} onValueChange={setLoginTag}>
                      <SelectTrigger className="bg-zinc-950/50 border-white/10" data-testid="login-tag">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-white/10">
                        <SelectItem value="both">Business and personal contacts</SelectItem>
                        <SelectItem value="business">Business contacts only</SelectItem>
                        <SelectItem value="personal">Personal contacts only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <Button 
                    type="submit"
                    disabled={connecting || !loginEmail || !loginPassword}
                    className="w-full bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500"
                    data-testid="connect-btn"
                  >
                    {connecting ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Lock className="w-4 h-4 mr-2" />
                    )}
                    Connect securely
                  </Button>
                </form>
                )}

                {/* Account List */}
                <div className="space-y-3 mt-6">
                  {accounts.length > 0 && (
                    <p className="text-xs text-zinc-500 font-medium uppercase tracking-wider">Connected Accounts</p>
                  )}
                  <AnimatePresence>
                    {accounts.map((account) => (
                      <motion.div
                        key={account.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="p-4 rounded-lg border border-white/10 bg-zinc-950/50 hover:border-white/20 transition-colors"
                        data-testid={`account-${account.id}`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            {['gmail', 'google'].includes(account.provider) ? <GoogleIcon /> :
                              ['outlook', 'hotmail', 'microsoft'].includes(account.provider) ? <MicrosoftIcon /> :
                              <Mail className="h-5 w-5 text-indigo-400" />}
                            <div>
                              <p className="font-medium text-white text-sm">{account.name || 'Unknown'}</p>
                              <p className="text-xs text-zinc-500">{account.email}</p>
                            </div>
                          </div>
                          
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <ChevronDown className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="bg-zinc-900 border-white/10">
                              <DropdownMenuItem onClick={() => setEditingAccount(account)} className="text-zinc-300 focus:text-white focus:bg-white/5">
                                <Tag className="w-4 h-4 mr-2" />
                                Change Tag
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => disconnectAccount(account.id)}
                                className="text-red-400 focus:text-red-300 focus:bg-red-500/10"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Disconnect
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>

                        <div className="flex items-center gap-2 mb-3">
                          <Badge 
                            variant="outline" 
                            className={`text-xs ${
                              account.tag === 'business' ? 'border-emerald-500/50 text-emerald-400' :
                              account.tag === 'personal' ? 'border-orange-500/50 text-orange-400' :
                              'border-indigo-500/50 text-indigo-400'
                            }`}
                          >
                            {account.tag === 'business' && <Building2 className="w-3 h-3 mr-1" />}
                            {account.tag === 'personal' && <User className="w-3 h-3 mr-1" />}
                            {account.tag === 'both' && <Users className="w-3 h-3 mr-1" />}
                            {account.tag}
                          </Badge>
                        </div>

                        <div className="flex gap-2">
                          <Button 
                            size="sm"
                            onClick={() => extractContacts(account.id, 'all')}
                            disabled={extracting}
                            className="w-full text-xs bg-gradient-to-r from-indigo-600 to-violet-600"
                            data-testid={`extract-all-${account.id}`}
                          >
                            {extracting ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                            Extract Contacts
                          </Button>
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contacts Table */}
          <div className="min-w-0 lg:col-span-8">
            <Card className="min-w-0 overflow-hidden bg-zinc-900/50 border-white/10" data-testid="contacts-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-['Outfit'] text-lg text-white">Extracted Contacts</CardTitle>
                    <CardDescription>
                      {filteredContacts.length} contacts {selectedContacts.size > 0 && `(${selectedContacts.size} selected)`}
                    </CardDescription>
                  </div>
                </div>
                
                {/* Filters */}
                <div className="flex flex-wrap gap-3 mt-4">
                  <div className="relative flex-1 min-w-[200px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input
                      placeholder="Search by name, email, or domain..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9 bg-zinc-950/50 border-white/10"
                      data-testid="search-input"
                    />
                  </div>
                  
                  <Select value={filterType} onValueChange={setFilterType}>
                    <SelectTrigger className="w-[140px] bg-zinc-950/50 border-white/10" data-testid="filter-type">
                      <Filter className="w-4 h-4 mr-2" />
                      <SelectValue placeholder="Type" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-white/10">
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="business">Business</SelectItem>
                      <SelectItem value="personal">Personal</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  <Select value={filterDomain || "all-domains"} onValueChange={(v) => setFilterDomain(v === "all-domains" ? "" : v)}>
                    <SelectTrigger className="w-[160px] bg-zinc-950/50 border-white/10" data-testid="filter-domain">
                      <Globe className="w-4 h-4 mr-2" />
                      <SelectValue placeholder="Domain" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-white/10 max-h-[300px]">
                      <SelectItem value="all-domains">All Domains</SelectItem>
                      {domains.map(domain => (
                        <SelectItem key={domain} value={domain}>{domain}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              
              <CardContent className="overflow-x-auto">
                <ScrollArea className="h-[500px] min-w-[680px]">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/10 hover:bg-transparent">
                        <TableHead className="w-12">
                          <Checkbox
                            checked={selectedContacts.size === filteredContacts.length && filteredContacts.length > 0}
                            onCheckedChange={toggleSelectAll}
                            data-testid="select-all-checkbox"
                          />
                        </TableHead>
                        <TableHead className="text-zinc-400">Name</TableHead>
                        <TableHead className="text-zinc-400">Email</TableHead>
                        <TableHead className="text-zinc-400">Domain</TableHead>
                        <TableHead className="text-zinc-400">Type</TableHead>
                        <TableHead className="text-zinc-400">Source</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {loading ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-12">
                            <Loader2 className="w-6 h-6 animate-spin mx-auto text-indigo-500" />
                            <p className="text-sm text-zinc-500 mt-2">Loading contacts...</p>
                          </TableCell>
                        </TableRow>
                      ) : filteredContacts.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-12">
                            <FileSpreadsheet className="w-12 h-12 mx-auto text-zinc-600 mb-3" />
                            <p className="text-zinc-500">No contacts found</p>
                            <p className="text-xs text-zinc-600 mt-1">Extract contacts from your connected accounts</p>
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredContacts.map((contact) => (
                          <TableRow 
                            key={contact.id} 
                            className="border-white/5 hover:bg-white/5"
                            data-testid={`contact-row-${contact.id}`}
                          >
                            <TableCell>
                              <Checkbox
                                checked={selectedContacts.has(contact.id)}
                                onCheckedChange={() => toggleContact(contact.id)}
                              />
                            </TableCell>
                            <TableCell className="font-medium text-white">{contact.name}</TableCell>
                            <TableCell className="text-zinc-400 font-mono text-sm">{contact.email}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="border-zinc-700 text-zinc-400 text-xs">
                                {contact.domain}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge 
                                className={`text-xs ${
                                  contact.is_business 
                                    ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' 
                                    : 'bg-orange-500/10 text-orange-400 border-orange-500/20'
                                }`}
                              >
                                {contact.is_business ? 'Business' : 'Personal'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="border-zinc-700 text-zinc-500 text-xs">
                                {contact.source}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </div>
          </>
        )}

        {/* Documents Tab */}
        {activeTab === 'documents' && (
          <>
            {/* Document Stats */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8"
            >
              <Card className="bg-zinc-900/50 border-white/10">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-zinc-500">Total Documents</p>
                      <p className="text-3xl font-bold font-['Outfit'] text-white">{documentStats?.total_documents || 0}</p>
                    </div>
                    <div className="w-12 h-12 rounded-lg bg-indigo-500/10 flex items-center justify-center">
                      <FileText className="w-6 h-6 text-indigo-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {['invoice', 'receipt', 'order', 'booking'].map((cat) => (
                <Card key={cat} className="bg-zinc-900/50 border-white/10">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-zinc-500 capitalize">{cat}s</p>
                        <p className="text-3xl font-bold font-['Outfit'] text-white">
                          {documentCategories[cat] || 0}
                        </p>
                      </div>
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${getCategoryColor(cat)}`}>
                        {getCategoryIcon(cat)}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Scan Documents Sidebar */}
              <div className="lg:col-span-4">
                <Card className="bg-zinc-900/50 border-white/10">
                  <CardHeader>
                    <CardTitle className="font-['Outfit'] text-lg text-white flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-indigo-400" />
                      Smart Document Scanner
                    </CardTitle>
                    <CardDescription>
                      Automatically extract invoices, receipts, orders & more from your emails
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Time Range Selector */}
                    <div className="p-3 rounded-lg bg-indigo-500/10 border border-indigo-500/20">
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-indigo-300 text-sm">Scan Time Range</Label>
                        <select
                          value={scanTimeRange}
                          onChange={(e) => setScanTimeRange(e.target.value)}
                          className="bg-zinc-900 border border-white/10 rounded px-2 py-1 text-sm text-white"
                          data-testid="scan-time-range"
                        >
                          <option value="30">Last 30 days</option>
                          <option value="90">Last 3 months</option>
                          <option value="180">Last 6 months</option>
                          <option value="365">Last year</option>
                        </select>
                      </div>
                      <div className="flex items-center justify-between">
                        <Label className="text-indigo-300 text-sm">Max Emails</Label>
                        <select
                          value={scanLimit}
                          onChange={(e) => setScanLimit(Number(e.target.value))}
                          className="bg-zinc-900 border border-white/10 rounded px-2 py-1 text-sm text-white"
                          data-testid="scan-limit"
                        >
                          <option value="50">50 emails</option>
                          <option value="100">100 emails</option>
                          <option value="200">200 emails</option>
                          <option value="500">500 emails</option>
                        </select>
                      </div>
                    </div>

                    {accounts.length === 0 ? (
                      <div className="text-center py-6 text-zinc-500">
                        <Mail className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p className="text-sm">Connect an account first</p>
                        <Button 
                          variant="link" 
                          onClick={() => setActiveTab('contacts')}
                          className="text-indigo-400"
                        >
                          Go to Contacts tab
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {accounts.map((account) => (
                          <div 
                            key={account.id}
                            className="p-4 rounded-lg border border-white/10 bg-zinc-950/50"
                          >
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2">
                                {['gmail', 'google'].includes(account.provider) ? <GoogleIcon /> :
                                  ['outlook', 'hotmail', 'microsoft'].includes(account.provider) ? <MicrosoftIcon /> :
                                  <Mail className="h-5 w-5 text-indigo-400" />}
                                <div>
                                  <p className="text-sm text-white">{account.email}</p>
                                  <p className="text-xs text-zinc-500">{account.provider}</p>
                                </div>
                              </div>
                            </div>
                            {account.needs_reconnect && (
                              <p className="text-xs text-amber-300 mb-3">
                                This older connection needs a fresh app password before scanning.
                              </p>
                            )}
                            <Button 
                              onClick={() => account.needs_reconnect
                                ? reconnectForDocumentScan(account)
                                : scanDocuments(account.id)}
                              disabled={scanningDocs}
                              className={account.needs_reconnect
                                ? "w-full bg-amber-600 hover:bg-amber-500"
                                : "w-full bg-gradient-to-r from-indigo-600 to-violet-600"}
                              data-testid={`scan-docs-${account.id}`}
                            >
                              {scanningDocs ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <Sparkles className="w-4 h-4 mr-2" />
                              )}
                              {account.needs_reconnect ? "Reconnect Account" : "Scan for Documents"}
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Category Filter */}
                    <div className="pt-4 border-t border-white/10">
                      <Label className="text-zinc-400 text-sm mb-2 block">Filter by Category</Label>
                      <div className="flex flex-wrap gap-2">
                        {['all', 'invoice', 'receipt', 'order', 'booking', 'payment', 'shipping', 'subscription', 'statement', 'ticket', 'reservation', 'contract', 'other'].map((cat) => (
                          <Badge
                            key={cat}
                            variant={documentCategory === cat ? 'default' : 'outline'}
                            className={`cursor-pointer ${documentCategory === cat ? 'bg-indigo-600' : 'border-white/10 hover:bg-white/5'}`}
                            onClick={() => setDocumentCategory(cat)}
                          >
                            {cat === 'all' ? 'All' : cat.charAt(0).toUpperCase() + cat.slice(1)}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Documents List */}
              <div className="lg:col-span-8">
                <Card className="bg-zinc-900/50 border-white/10">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="font-['Outfit'] text-lg text-white">Extracted Documents</CardTitle>
                        <CardDescription>{documents.length} documents found</CardDescription>
                      </div>
                      <Button 
                        onClick={exportDocuments}
                        disabled={documents.length === 0}
                        variant="outline"
                        className="border-white/10"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Export CSV
                      </Button>
                    </div>
                    
                    <div className="mt-4">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                        <Input
                          placeholder="Search documents..."
                          value={documentSearch}
                          onChange={(e) => setDocumentSearch(e.target.value)}
                          className="pl-9 bg-zinc-950/50 border-white/10"
                        />
                      </div>
                    </div>
                  </CardHeader>
                  
                  <CardContent>
                    <ScrollArea className="h-[500px]">
                      {documents.length === 0 ? (
                        <div className="text-center py-12">
                          <FileText className="w-12 h-12 mx-auto text-zinc-600 mb-3" />
                          <p className="text-zinc-500">No documents extracted yet</p>
                          <p className="text-xs text-zinc-600 mt-1">Scan your emails to find invoices, receipts & more</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {documents.map((doc) => (
                            <div
                              key={doc.id}
                              className="p-4 rounded-lg border border-white/10 bg-zinc-950/50 hover:border-white/20 transition-colors cursor-pointer"
                              onClick={() => setSelectedDocument(doc)}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex items-start gap-3">
                                  <div className={`p-2 rounded-lg ${getCategoryColor(doc.category)}`}>
                                    {getCategoryIcon(doc.category)}
                                  </div>
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <Badge className={getCategoryColor(doc.category)}>
                                        {doc.category}
                                      </Badge>
                                      {doc.extracted_data?.total && (
                                        <span className="text-sm font-semibold text-emerald-400">
                                          {doc.extracted_data.currency || '$'}{doc.extracted_data.total}
                                        </span>
                                      )}
                                    </div>
                                    <p className="font-medium text-white text-sm">{doc.summary || doc.subject}</p>
                                    <p className="text-xs text-zinc-500 mt-1">
                                      {doc.sender} • {doc.document_date || new Date(doc.extracted_at).toLocaleDateString()}
                                    </p>
                                  </div>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => { e.stopPropagation(); deleteDocument(doc.id); }}
                                  className="text-zinc-500 hover:text-red-400"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </ScrollArea>
                  </CardContent>
                </Card>
              </div>
            </div>
          </>
        )}
      </main>

      {/* Document Detail Dialog */}
      <Dialog open={!!selectedDocument} onOpenChange={() => setSelectedDocument(null)}>
        <DialogContent className="bg-zinc-900 border-white/10 max-w-2xl">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${getCategoryColor(selectedDocument?.category)}`}>
                {getCategoryIcon(selectedDocument?.category)}
              </div>
              <div>
                <DialogTitle className="text-white font-['Outfit']">
                  {selectedDocument?.category?.charAt(0).toUpperCase() + selectedDocument?.category?.slice(1)} Details
                </DialogTitle>
                <DialogDescription>{selectedDocument?.sender}</DialogDescription>
              </div>
            </div>
          </DialogHeader>
          
          {selectedDocument && (
            <div className="space-y-4 py-4">
              <div className="p-4 rounded-lg bg-zinc-950/50 border border-white/10">
                <p className="text-sm text-zinc-400 mb-2">Summary</p>
                <p className="text-white">{selectedDocument.summary}</p>
              </div>
              
              {selectedDocument.extracted_data && Object.keys(selectedDocument.extracted_data).length > 0 && (
                <div className="p-4 rounded-lg bg-zinc-950/50 border border-white/10">
                  <p className="text-sm text-zinc-400 mb-3">Extracted Data</p>
                  <div className="grid grid-cols-2 gap-3">
                    {Object.entries(selectedDocument.extracted_data).map(([key, value]) => (
                      <div key={key}>
                        <p className="text-xs text-zinc-500 capitalize">{key.replace('_', ' ')}</p>
                        <p className="text-sm text-white font-medium">
                          {Array.isArray(value) ? value.join(', ') : String(value)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-zinc-500">Email Subject</p>
                  <p className="text-white">{selectedDocument.subject}</p>
                </div>
                <div>
                  <p className="text-zinc-500">Email Date</p>
                  <p className="text-white">{selectedDocument.email_date}</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Account Tag Dialog */}
      <Dialog open={!!editingAccount} onOpenChange={() => setEditingAccount(null)}>
        <DialogContent className="bg-zinc-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white font-['Outfit']">Update Account Tag</DialogTitle>
            <DialogDescription>
              Choose how to classify this account
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-3 py-4">
            {['business', 'personal', 'both'].map((tag) => (
              <Button
                key={tag}
                variant={editingAccount?.tag === tag ? 'default' : 'outline'}
                onClick={() => updateAccountTag(editingAccount?.id, tag)}
                className={editingAccount?.tag === tag ? 'bg-indigo-600' : 'border-white/10'}
                data-testid={`tag-${tag}-btn`}
              >
                {tag === 'business' && <Building2 className="w-4 h-4 mr-2" />}
                {tag === 'personal' && <User className="w-4 h-4 mr-2" />}
                {tag === 'both' && <Users className="w-4 h-4 mr-2" />}
                {tag.charAt(0).toUpperCase() + tag.slice(1)}
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Export Settings Dialog */}
      <Dialog open={showExportSettings} onOpenChange={setShowExportSettings}>
        <DialogContent className="bg-zinc-900 border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white font-['Outfit']">CSV Export Settings</DialogTitle>
            <DialogDescription>
              Configure which columns to include and customize headers. Default format matches Email Promo Creator requirements.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Current Columns */}
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Export Columns (drag to reorder)</Label>
              <div className="space-y-2">
                {exportColumns.map((col, index) => (
                  <div 
                    key={col.field}
                    className="flex items-center gap-3 p-3 rounded-lg bg-zinc-950/50 border border-white/10"
                    data-testid={`export-col-${col.field}`}
                  >
                    <div className="flex flex-col gap-1">
                      <button 
                        onClick={() => moveColumn(index, 'up')}
                        disabled={index === 0}
                        className="p-0.5 hover:bg-white/10 rounded disabled:opacity-30"
                      >
                        <ArrowUp className="w-3 h-3" />
                      </button>
                      <button 
                        onClick={() => moveColumn(index, 'down')}
                        disabled={index === exportColumns.length - 1}
                        className="p-0.5 hover:bg-white/10 rounded disabled:opacity-30"
                      >
                        <ArrowDown className="w-3 h-3" />
                      </button>
                    </div>
                    
                    <Switch
                      checked={col.enabled}
                      onCheckedChange={() => toggleExportColumn(col.field)}
                      data-testid={`toggle-${col.field}`}
                    />
                    
                    <div className="flex-1 grid grid-cols-2 gap-2">
                      <div>
                        <p className="text-xs text-zinc-500 mb-1">Field</p>
                        <p className="text-sm text-white capitalize">{col.field.replace('_', ' ')}</p>
                      </div>
                      <div>
                        <p className="text-xs text-zinc-500 mb-1">CSV Header</p>
                        <Input
                          value={col.header}
                          onChange={(e) => updateColumnHeader(col.field, e.target.value)}
                          className="h-8 text-sm bg-zinc-900 border-white/10"
                          data-testid={`header-${col.field}`}
                        />
                      </div>
                    </div>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeExportColumn(col.field)}
                      className="h-8 w-8 p-0 text-zinc-500 hover:text-red-400"
                      data-testid={`remove-${col.field}`}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Add Column */}
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Add Column</Label>
              <div className="flex flex-wrap gap-2">
                {availableFields
                  .filter(f => !exportColumns.find(c => c.field === f.field))
                  .map(field => (
                    <Button
                      key={field.field}
                      variant="outline"
                      size="sm"
                      onClick={() => addExportColumn(field.field)}
                      className="border-white/10 hover:bg-white/5 text-xs"
                      data-testid={`add-${field.field}`}
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      {field.label}
                    </Button>
                  ))}
              </div>
            </div>

            {/* Preview */}
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Preview (first row)</Label>
              <div className="p-3 rounded-lg bg-zinc-950 border border-white/10 font-mono text-xs overflow-x-auto">
                <span className="text-indigo-400">
                  {exportColumns.filter(c => c.enabled).map(c => c.header).join(',')}
                </span>
              </div>
            </div>
          </div>

          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={resetToDefault}
              className="border-white/10"
              data-testid="reset-default-btn"
            >
              Reset to Default
            </Button>
            <Button
              onClick={exportCSV}
              disabled={contacts.length === 0 || exportColumns.filter(c => c.enabled).length === 0}
              className="bg-gradient-to-r from-indigo-600 to-violet-600"
              data-testid="export-now-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export Now
            </Button>
            {targetGroupId && (
              <Button
                onClick={pushSelectedToProEmail}
                disabled={contacts.length === 0 || pushingToProEmail}
                className="bg-emerald-600 hover:bg-emerald-500"
                data-testid="push-to-pro-email-btn"
              >
                {pushingToProEmail ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <ExternalLink className="w-4 h-4 mr-2" />
                )}
                Push to Pro-Email
                {selectedContacts.size > 0 && (
                  <Badge variant="secondary" className="ml-2 bg-white/20">
                    {selectedContacts.size}
                  </Badge>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sync Settings Dialog */}
      <Dialog open={showSyncSettings} onOpenChange={setShowSyncSettings}>
        <DialogContent className="bg-zinc-900 border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white font-['Outfit']">Auto Sync Settings</DialogTitle>
            <DialogDescription>
              Automatically extract new contacts from your email accounts and get notified about new business leads.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Enable/Disable */}
            <div className="flex items-center justify-between p-4 rounded-lg bg-zinc-950/50 border border-white/10">
              <div>
                <p className="font-medium text-white">Enable Auto Sync</p>
                <p className="text-sm text-zinc-500">Automatically extract contacts on schedule</p>
              </div>
              <Switch
                checked={syncSettings.enabled}
                onCheckedChange={(enabled) => updateSyncSettings({ enabled })}
                data-testid="sync-enabled-toggle"
              />
            </div>

            {/* Frequency */}
            <div className="space-y-2">
              <Label className="text-zinc-400">Sync Frequency</Label>
              <Select 
                value={syncSettings.frequency} 
                onValueChange={(frequency) => updateSyncSettings({ frequency })}
                disabled={!syncSettings.enabled}
              >
                <SelectTrigger className="bg-zinc-950/50 border-white/10" data-testid="sync-frequency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10">
                  <SelectItem value="hourly">Every Hour</SelectItem>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Sync Time (for daily/weekly) */}
            {syncSettings.frequency !== 'hourly' && (
              <div className="space-y-2">
                <Label className="text-zinc-400">Sync Time</Label>
                <Input
                  type="time"
                  value={syncSettings.sync_time}
                  onChange={(e) => updateSyncSettings({ sync_time: e.target.value })}
                  disabled={!syncSettings.enabled}
                  className="bg-zinc-950/50 border-white/10"
                  data-testid="sync-time"
                />
              </div>
            )}

            {/* Sync Day (for weekly) */}
            {syncSettings.frequency === 'weekly' && (
              <div className="space-y-2">
                <Label className="text-zinc-400">Sync Day</Label>
                <Select 
                  value={String(syncSettings.sync_day)} 
                  onValueChange={(day) => updateSyncSettings({ sync_day: parseInt(day) })}
                  disabled={!syncSettings.enabled}
                >
                  <SelectTrigger className="bg-zinc-950/50 border-white/10" data-testid="sync-day">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    <SelectItem value="0">Monday</SelectItem>
                    <SelectItem value="1">Tuesday</SelectItem>
                    <SelectItem value="2">Wednesday</SelectItem>
                    <SelectItem value="3">Thursday</SelectItem>
                    <SelectItem value="4">Friday</SelectItem>
                    <SelectItem value="5">Saturday</SelectItem>
                    <SelectItem value="6">Sunday</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Extract Source */}
            <div className="space-y-2">
              <Label className="text-zinc-400">Extract From</Label>
              <Select 
                value={syncSettings.auto_extract_source} 
                onValueChange={(source) => updateSyncSettings({ auto_extract_source: source })}
                disabled={!syncSettings.enabled}
              >
                <SelectTrigger className="bg-zinc-950/50 border-white/10" data-testid="sync-source">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10">
                  <SelectItem value="all">Contacts & Inbox</SelectItem>
                  <SelectItem value="contacts">Contacts Only</SelectItem>
                  <SelectItem value="inbox">Inbox Only</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Notify New Business */}
            <div className="flex items-center justify-between p-4 rounded-lg bg-zinc-950/50 border border-white/10">
              <div>
                <p className="font-medium text-white flex items-center gap-2">
                  <Bell className="w-4 h-4 text-indigo-400" />
                  Notify New Business Contacts
                </p>
                <p className="text-sm text-zinc-500">Get notified when new business emails are found</p>
              </div>
              <Switch
                checked={syncSettings.notify_new_business}
                onCheckedChange={(notify) => updateSyncSettings({ notify_new_business: notify })}
                disabled={!syncSettings.enabled}
                data-testid="notify-business-toggle"
              />
            </div>

            {/* Sync History */}
            {syncHistory.length > 0 && (
              <div className="space-y-2">
                <Label className="text-zinc-400 flex items-center gap-2">
                  <History className="w-4 h-4" />
                  Recent Sync History
                </Label>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {syncHistory.map((sync) => (
                    <div 
                      key={sync.id}
                      className="p-3 rounded-lg bg-zinc-950/50 border border-white/5 text-sm"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-white">
                          {sync.new_contacts} new contacts
                          {sync.new_business_contacts > 0 && (
                            <Badge className="ml-2 bg-emerald-500/20 text-emerald-400 text-xs">
                              {sync.new_business_contacts} business
                            </Badge>
                          )}
                        </span>
                        <span className={`text-xs ${sync.status === 'completed' ? 'text-emerald-400' : 'text-yellow-400'}`}>
                          {sync.status}
                        </span>
                      </div>
                      <p className="text-xs text-zinc-500 mt-1">
                        {new Date(sync.completed_at).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              onClick={triggerSyncNow}
              disabled={syncing || accounts.length === 0}
              className="bg-gradient-to-r from-indigo-600 to-violet-600"
              data-testid="sync-now-dialog-btn"
            >
              {syncing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Play className="w-4 h-4 mr-2" />
              )}
              Sync Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Help Dialog - App Password Instructions */}
      <Dialog open={showHelp} onOpenChange={setShowHelp}>
        <DialogContent className="bg-zinc-900 border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white font-['Outfit']">How to Get Your App Password</DialogTitle>
            <DialogDescription>
              For security, use an App Password instead of your regular password.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Gmail Instructions */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <GoogleIcon />
                <h3 className="font-semibold text-white">Gmail / Google Account</h3>
              </div>
              <ol className="space-y-2 text-sm text-zinc-400 list-decimal list-inside">
                <li>Go to <a href="https://myaccount.google.com/security" target="_blank" rel="noopener noreferrer" className="text-indigo-400 hover:underline">myaccount.google.com/security</a></li>
                <li>Enable <strong className="text-white">2-Step Verification</strong> (required)</li>
                <li>Search for <strong className="text-white">"App passwords"</strong> in security settings</li>
                <li>Select "Mail" and "Windows Computer"</li>
                <li>Click <strong className="text-white">Generate</strong></li>
                <li>Copy the 16-character password shown</li>
              </ol>
            </div>

            {/* Outlook Instructions */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <MicrosoftIcon />
                <h3 className="font-semibold text-white">Outlook / Microsoft 365</h3>
              </div>
              <ol className="space-y-2 text-sm text-zinc-400 list-decimal list-inside">
                <li>Go to <a href="https://account.microsoft.com/security" target="_blank" rel="noopener noreferrer" className="text-indigo-400 hover:underline">account.microsoft.com/security</a></li>
                <li>Enable <strong className="text-white">Two-step verification</strong></li>
                <li>Click <strong className="text-white">App passwords</strong></li>
                <li>Create a new app password</li>
                <li>Copy the generated password</li>
              </ol>
              <p className="text-xs text-zinc-500 mt-2">
                Note: For Microsoft 365 work/school accounts, your admin may need to enable app passwords.
              </p>
            </div>

            <div className="p-3 rounded-lg bg-indigo-500/10 border border-indigo-500/20">
              <p className="text-sm text-indigo-300">
                <strong>Tip:</strong> Your app password is different from your regular password. 
                It's a special password that allows secure access to your email.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={() => setShowHelp(false)} className="bg-zinc-800 hover:bg-zinc-700">
              Got it!
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
        </>
      )}
    </div>
  );
}

export default App;
